/**
 * AdminConfigResolver: resolves typed config for staff-global admin slugs.
 *
 * Sibling to ConfigResolver but scoped to admin_configs rows, which have no
 * domain/client layer, no requires[] graph, and no feature toggles to honour.
 *
 * Resolution order per slug:
 * 1. Schema defaults (registry SLUG_TO_DEFAULTS)
 * 2. config.configs.default_config
 * 3. admin_configs row override (applied via merge_strategy)
 */

import { logger } from '../../utils/core/logger';
import { applyMergeStrategy } from './merge';
import { SLUG_TO_DEFAULTS } from './registry.generated';
import type { AdminConfigRow, ConfigDefinition } from './types';

export class AdminConfigResolver {
    private _definitions: Map<string, ConfigDefinition>;
    private _overrides: Map<string, AdminConfigRow>;
    private _cache: Map<string, unknown>;

    constructor(definitions: ConfigDefinition[], adminConfigs: AdminConfigRow[]) {
        this._definitions = new Map(
            definitions.filter((d) => d.scope === 'admin').map((d) => [d.slug, d])
        );
        this._overrides = new Map(adminConfigs.map((ac) => [ac.config_slug, ac]));
        this._cache = new Map();
    }

    /**
     * Resolve and return the typed config for an admin slug.
     * Returns null if the slug is unknown.
     */
    getConfig<T>(slug: string): T | null {
        if (this._cache.has(slug)) return this._cache.get(slug) as T | null;
        const result = this._resolve<T>(slug);
        this._cache.set(slug, result);
        return result;
    }

    private _resolve<T>(slug: string): T | null {
        const defn = this._definitions.get(slug);
        if (!defn) {
            logger.warn(`Unknown admin config slug: ${slug}`);
            return null;
        }

        const override = this._overrides.get(slug);
        const merged = applyMergeStrategy(
            defn.merge_strategy,
            defn.default_config,
            override?.config ?? null
        );

        const defaults = SLUG_TO_DEFAULTS[slug];
        if (defaults && Object.keys(defaults).length > 0) {
            return applyMergeStrategy('deep_merge', defaults, merged) as T;
        }
        return merged as T;
    }
}
